import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  HiCash,
  HiCreditCard,
  HiClock,
  HiCheckCircle,
  HiArrowLeft,
  HiCalendar
} from 'react-icons/hi';
import { useGeneral } from '../../../contexts';
import Services from '../../../services/Services';
import { PaymentBalanceResult, PaymentDetail } from '../../../types/service.types';
import Button from '../../../components/ui/Button/Button';
import LoadingSkeleton from '../../../components/ui/LoadingSkeleton/LoadingSkeleton';
import EmptyState from '../../../components/ui/EmptyState/EmptyState';
import Modal from '../../../components/ui/Modal/Modal';
import { Table, TableHeader, TableBody, TableRow, TableCell, EmptyTableRow } from '../../../components/ui/Table';
import { formatEventDateRange } from '../../../utils/dateFormatters';
import styles from './CommunityRevenuePage.module.css';
import { OrganizerPanelOutletContext } from '../../../types/organizerPanel.types';

interface EventListEntry {
  eventID: string;
  title: string;
  cover: string;
  eventStartDateTime: number;
  eventEndDateTime: number;
  payments: PaymentDetail[];
  totalBalance: number;
  withdrawablePaymentIDs: string[];
  withdrawableAmount: number;
}

const CommunityRevenuePage: React.FC = () => {
  const { channel } = useOutletContext<OrganizerPanelOutletContext>();
  const { auth, language, toastSuccess, toastError } = useGeneral();
  const { token } = auth;
  const { getTranslation, language: currentLanguage } = language;

  const [view, setView] = useState<'main' | 'eventDetail'>('main');
  const [selectedEventID, setSelectedEventID] = useState<string | null>(null);
  const [balanceData, setBalanceData] = useState<PaymentBalanceResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [withdrawing, setWithdrawing] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawTarget, setWithdrawTarget] = useState<{
    paymentIDs: string[];
    totalAmount: number;
    eventTitle?: string;
  } | null>(null);

  const fetchBalanceData = async () => {
    if (!token || !channel?.channelID) return;

    try {
      const response = await Services.SocialHiveGetMyPaymentBalance(token, channel.channelID);
      if (response.data.status && response.data.result) {
        setBalanceData(response.data.result);
      } else {
        setBalanceData(null);
      }
    } catch (error: any) {
      if (error.name === 'AbortError' || error.name === 'CanceledError') return;
      toastError(getTranslation('organizerPanel.fetchBalanceError') || 'Failed to load balance information');
      setBalanceData(null);
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await fetchBalanceData();
      setLoading(false);
    };
    load();
  }, [token, channel?.channelID]);

  const totalEarnings = useMemo(() => {
    if (!balanceData) return 0;
    return balanceData.totalBalance + balanceData.totalWithDraw + balanceData.totalTransferredToIBAN;
  }, [balanceData]);

  const eventsList = useMemo((): EventListEntry[] => {
    if (!balanceData?.paymentDetails) return [];

    return Object.entries(balanceData.paymentDetails)
      .map(([eventID, group]) => {
        const withdrawablePayments = group.payments.filter(p => p.paymentStatus === 'In Balance');
        const totalBalance = group.payments.reduce((sum, p) => sum + p.balance, 0);

        return {
          eventID,
          title: group.infos.title,
          cover: group.infos.cover,
          eventStartDateTime: parseInt(group.infos.eventStartDateTime, 10) || 0,
          eventEndDateTime: parseInt(group.infos.eventEndDateTime, 10) || 0,
          payments: group.payments,
          totalBalance,
          withdrawablePaymentIDs: withdrawablePayments.map(p => p.paymentID),
          withdrawableAmount: withdrawablePayments.reduce((sum, p) => sum + p.balance, 0),
        };
      })
      .sort((a, b) => b.eventStartDateTime - a.eventStartDateTime);
  }, [balanceData]);

  const selectedEvent = useMemo(() => {
    if (!selectedEventID) return null;
    return eventsList.find(e => e.eventID === selectedEventID) || null;
  }, [eventsList, selectedEventID]);

  const formatCurrency = (amount: number): string => {
    return `${amount.toLocaleString('en-US')} TL`;
  };

  const getCommissionPercent = (payment: PaymentDetail): number => {
    if (!payment.commissionRate) return 0;
    return Math.round((1 - payment.commissionRate) * 100);
  };

  const getStatusBadgeClass = (status: string): string => {
    if (status === 'In Balance') return styles.statusInBalance;
    if (status === 'Withdraw Requested') return styles.statusWithdrawRequested;
    if (status === 'Transferred') return styles.statusTransferred;
    return styles.statusInBalance;
  };

  const getStatusLabel = (status: string): string => {
    if (status === 'In Balance') return getTranslation('organizerPanel.inBalance') || 'In Balance';
    if (status === 'Withdraw Requested') return getTranslation('organizerPanel.withdrawRequested') || 'Withdraw Requested';
    if (status === 'Transferred') return getTranslation('organizerPanel.transferredStatus') || 'Transferred';
    return status;
  };

  // Withdraw handlers
  const handleEventWithdraw = (e: React.MouseEvent, event: EventListEntry) => {
    e.stopPropagation();
    if (event.withdrawablePaymentIDs.length === 0) return;

    setWithdrawTarget({
      paymentIDs: event.withdrawablePaymentIDs,
      totalAmount: event.withdrawableAmount,
      eventTitle: event.title,
    });
    setShowWithdrawModal(true);
  };

  const handlePaymentWithdraw = (payment: PaymentDetail) => {
    setWithdrawTarget({
      paymentIDs: [payment.paymentID],
      totalAmount: payment.balance,
    });
    setShowWithdrawModal(true);
  };

  const handleWithdrawAllForEvent = () => {
    if (!selectedEvent || selectedEvent.withdrawablePaymentIDs.length === 0) return;

    setWithdrawTarget({
      paymentIDs: selectedEvent.withdrawablePaymentIDs,
      totalAmount: selectedEvent.withdrawableAmount,
      eventTitle: selectedEvent.title,
    });
    setShowWithdrawModal(true);
  };

  const handleConfirmWithdraw = async () => {
    if (!token || !withdrawTarget) return;
    setWithdrawing(true);

    try {
      const response = await Services.SocialHiveWithdrawBalance(token, withdrawTarget.paymentIDs);
      if (response.data.status) {
        toastSuccess(getTranslation('organizerPanel.withdrawSuccess') || 'Withdrawal requested successfully');
        await fetchBalanceData();
      } else {
        toastError(response.data.message || getTranslation('organizerPanel.withdrawError') || 'Withdrawal failed');
      }
    } catch (error: any) {
      toastError(error?.response?.data?.message || getTranslation('organizerPanel.withdrawError') || 'Withdrawal failed');
    } finally {
      setWithdrawing(false);
      setShowWithdrawModal(false);
      setWithdrawTarget(null);
    }
  };

  const handleEventClick = (event: EventListEntry) => {
    setSelectedEventID(event.eventID);
    setView('eventDetail');
  };

  const handleBackToEvents = () => {
    setView('main');
    setSelectedEventID(null);
  };

  // Summary cards renderer
  const renderSummaryCards = () => {
    if (loading) {
      return (
        <div className={styles.metricsGrid}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className={styles.metricCard}>
              <div className={styles.metricContent}>
                <LoadingSkeleton height={12} width="60%" />
                <LoadingSkeleton height={24} width="80%" />
                <LoadingSkeleton height={10} width="40%" />
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className={styles.metricsGrid}>
        <div className={`${styles.metricCard} ${styles.metricCardAnimated}`}>
          <div className={styles.metricIcon}>
            {/* @ts-ignore */}
            <HiCash size={20} />
          </div>
          <div className={styles.metricContent}>
            <span className={styles.metricLabel}>{getTranslation('organizerPanel.totalEarnings') || 'Total Earnings'}</span>
            <span className={styles.metricValue}>{formatCurrency(totalEarnings)}</span>

          </div>
        </div>

        <div className={`${styles.metricCard} ${styles.metricCardAnimated}`}>
          <div className={styles.metricIcon}>
            {/* @ts-ignore */}
            <HiCreditCard size={20} />
          </div>
          <div className={styles.metricContent}>
            <span className={styles.metricLabel}>{getTranslation('organizerPanel.availableBalance') || 'Available Balance'}</span>
            <span className={styles.metricValue}>{formatCurrency(balanceData?.totalBalance || 0)}</span>

          </div>
        </div>

        <div className={`${styles.metricCard} ${styles.metricCardAnimated}`}>
          <div className={styles.metricIcon}>
            {/* @ts-ignore */}
            <HiClock size={20} />
          </div>
          <div className={styles.metricContent}>
            <span className={styles.metricLabel}>{getTranslation('organizerPanel.pending') || 'Pending'}</span>
            <span className={styles.metricValue}>{formatCurrency(balanceData?.totalWithDraw || 0)}</span>

          </div>
        </div>

        <div className={`${styles.metricCard} ${styles.metricCardAnimated}`}>
          <div className={styles.metricIcon}>
            {/* @ts-ignore */}
            <HiCheckCircle size={20} />
          </div>
          <div className={styles.metricContent}>
            <span className={styles.metricLabel}>{getTranslation('organizerPanel.transferred') || 'Transferred'}</span>
            <span className={styles.metricValue}>{formatCurrency(balanceData?.totalTransferredToIBAN || 0)}</span>

          </div>
        </div>
      </div>
    );
  };

  // Main view: events list
  const renderMainView = () => {
    return (
      <div key="main-view" className={styles.slideInLeft}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>
            {getTranslation('organizerPanel.eventsWithRevenue') || 'Events with Revenue'}
          </h3>
        </div>

        {loading ? (
          <div className={styles.eventsList}>
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className={styles.skeletonEventCard}>
                <LoadingSkeleton height={64} width={114} variant="rectangular" />
                <div className={styles.skeletonEventInfo}>
                  <LoadingSkeleton height={16} width="50%" />
                  <LoadingSkeleton height={14} width="35%" />
                  <LoadingSkeleton height={12} width="20%" />
                </div>
                <LoadingSkeleton height={20} width={80} />
              </div>
            ))}
          </div>
        ) : eventsList.length === 0 ? (
          <EmptyState
            title={getTranslation('organizerPanel.noRevenueData') || 'No Revenue Data'}
            description={getTranslation('organizerPanel.noRevenueDataDescription') || 'There are no payments or revenue for this community yet.'}
          />
        ) : (
          <div className={styles.eventsList}>
            {eventsList.map((event) => (
              <div
                key={event.eventID}
                className={`${styles.eventCard} ${styles.eventCardAnimated}`}
                onClick={() => handleEventClick(event)}
              >
                {event.cover ? (
                  <img
                    src={event.cover}
                    alt={event.title}
                    className={styles.eventCover}
                  />
                ) : (
                  <div className={styles.eventCoverPlaceholder}>
                    {/* @ts-ignore */}
                    <HiCalendar size={24} />
                  </div>
                )}

                <div className={styles.eventInfo}>
                  <p className={styles.eventTitle}>{event.title}</p>
                  <div className={styles.eventDate}>
                    {event.eventStartDateTime ? formatEventDateRange(event.eventStartDateTime, event.eventEndDateTime, currentLanguage) : ''}
                  </div>
                  <div className={styles.eventPaymentCount}>
                    {event.payments.length} {event.payments.length === 1
                      ? (getTranslation('organizerPanel.paymentSingular') || 'payment')
                      : (getTranslation('organizerPanel.paymentPlural') || 'payments')}
                  </div>
                </div>

                <div className={styles.eventRight}>
                  <div className={styles.eventBalance}>
                    <div className={styles.eventBalanceAmount}>
                      {formatCurrency(event.totalBalance)}
                    </div>
                  </div>

                  {event.withdrawablePaymentIDs.length > 0 && (
                    <Button
                      variant="primary"
                      size="small"
                      onClick={(e: React.MouseEvent) => handleEventWithdraw(e, event)}
                    >
                      {getTranslation('organizerPanel.withdraw') || 'Withdraw'}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Event detail view: payment table
  const renderEventDetailView = () => {
    if (!selectedEvent) return null;

    return (
      <div key="detail-view" className={styles.slideInRight}>
        <button className={styles.backButton} onClick={handleBackToEvents}>
          {/* @ts-ignore */}
          <HiArrowLeft size={16} />
          {getTranslation('organizerPanel.backToEvents') || 'Back to Events'}
        </button>

        <div className={styles.eventDetailHeader}>
          {selectedEvent.cover ? (
            <img
              src={selectedEvent.cover}
              alt={selectedEvent.title}
              className={styles.eventDetailCover}
            />
          ) : (
            <div className={`${styles.eventCoverPlaceholder} ${styles.eventDetailCover}`}>
              {/* @ts-ignore */}
              <HiCalendar size={32} />
            </div>
          )}
          <div className={styles.eventDetailInfo}>
            <h3 className={styles.eventDetailTitle}>{selectedEvent.title}</h3>
            <div className={styles.eventDetailDate}>
              {selectedEvent.eventStartDateTime ? formatEventDateRange(selectedEvent.eventStartDateTime, selectedEvent.eventEndDateTime, currentLanguage) : ''}
            </div>
          </div>
        </div>

        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>
            {getTranslation('organizerPanel.eventPayments') || 'Event Payments'}
          </h3>
          {selectedEvent.withdrawablePaymentIDs.length > 0 && (
            <Button
              variant="primary"
              size="small"
              onClick={handleWithdrawAllForEvent}
            >
              {getTranslation('organizerPanel.withdrawAll') || 'Withdraw All'}
            </Button>
          )}
        </div>

        <Table>
            <TableHeader
              columns={[
                getTranslation('organizerPanel.paymentId') || 'Payment ID',
                getTranslation('organizerPanel.paymentStatus') || 'Status',
                getTranslation('organizerPanel.amount') || 'Amount',
                getTranslation('organizerPanel.commission') || 'Commission',
                getTranslation('organizerPanel.action') || 'Action'
              ]}
              columnWidths={['1.5fr', '1fr', '1fr', '1fr', '100px']}
            />
            <TableBody>
              {selectedEvent.payments.length === 0 ? (
                <EmptyTableRow message={getTranslation('organizerPanel.noRevenueData') || 'No payments'} />
              ) : (
                selectedEvent.payments.map((payment) => (
                  <TableRow
                    key={payment.paymentID}
                    columnWidths={['1.5fr', '1fr', '1fr', '1fr', '100px']}
                  >
                    <TableCell>
                      <span className={styles.paymentIdCell} title={payment.paymentID}>
                        ...{payment.paymentID.slice(-8)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={`${styles.statusBadge} ${getStatusBadgeClass(payment.paymentStatus)}`}>
                        {getStatusLabel(payment.paymentStatus)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={styles.amountCell}>
                        {formatCurrency(payment.balance)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={styles.commissionCell}>
                        {getCommissionPercent(payment)}%
                      </span>
                    </TableCell>
                    <TableCell>
                      {payment.paymentStatus === 'In Balance' ? (
                        <Button
                          variant="primary"
                          size="small"
                          onClick={() => handlePaymentWithdraw(payment)}
                        >
                          {getTranslation('organizerPanel.withdraw') || 'Withdraw'}
                        </Button>
                      ) : (
                        <span>—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <p className={styles.commissionNote}>
            {getTranslation('organizerPanel.commissionNote') || 'Commission rate may change after 45 days since payment.'}
          </p>
      </div>
    );
  };

  return (
    <div className={styles.communityRevenuePage}>
      <div className={styles.header}>
        <h2 className={styles.pageTitle}>
          {getTranslation('organizerPanel.communityRevenue') || 'Community Revenue'}
        </h2>
      </div>

      {renderSummaryCards()}

      {view === 'main' ? renderMainView() : renderEventDetailView()}

      {/* Withdraw Confirmation Modal */}
      <Modal
        isOpen={showWithdrawModal}
        onClose={() => {
          if (!withdrawing) {
            setShowWithdrawModal(false);
            setWithdrawTarget(null);
          }
        }}
        onConfirm={handleConfirmWithdraw}
        variant="confirm"
        title={getTranslation('organizerPanel.withdrawConfirmTitle') || 'Confirm Withdrawal'}
        message={
          withdrawTarget?.eventTitle
            ? (getTranslation('organizerPanel.withdrawConfirmMessage') || 'Are you sure you want to request withdrawal for {count} payment(s) totaling {amount}?')
                .replace('{count}', String(withdrawTarget.paymentIDs.length))
                .replace('{amount}', formatCurrency(withdrawTarget.totalAmount))
            : (getTranslation('organizerPanel.withdrawSingleConfirmMessage') || 'Are you sure you want to request withdrawal for {amount}?')
                .replace('{amount}', formatCurrency(withdrawTarget?.totalAmount || 0))
        }
        confirmText={getTranslation('organizerPanel.withdraw') || 'Withdraw'}
        confirmVariant="primary"
        cancelText={getTranslation('common.cancel') || 'Cancel'}
      />
    </div>
  );
};

export default CommunityRevenuePage;
